import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAllCollectionsAsync, getCollectionByIdAsync } from '../utils/inventoryService';
import PaymentModal from './PaymentModal';
import ImageLightbox from './ImageLightbox';
import FavoriteButton from './FavoriteButton';
import LazyImage from './LazyImage';
import useDeviceDetection from '../hooks/useDeviceDetection';

const CollectionContainer = styled.div`
  padding: 0 0 40px 0;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  margin-bottom: 5px;
`;

const NavLink = styled(Link)`
  display: inline-block;
  font-size: 0.9rem;
  font-weight: 500;
  color: #aaa;
  transition: color 0.2s ease-in-out;
  padding: 6px 12px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-left: 10px;

  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:first-child {
    margin-left: 0;
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const CollectionTitle = styled.h1`
  font-size: 2rem;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CollectionInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const CollectionDetails = styled.div`
  flex: 1;
  min-width: 250px;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  color: #aaa;
`;

const DetailItem = styled.li`
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const StatusBadge = styled.div`
  display: inline-block;
  background: #a00;
  color: white;
  padding: 5px 15px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #2c7d46;
  color: white;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 40px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const PhotomontageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  background-color: #000;
  padding: 10px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 30px;
    padding: 15px;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    overflow: visible;
    margin-bottom: 20px;
  }
`;

const PhotomontageItem = styled.div`
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  transition: transform 0.3s ease-in-out;
  background-color: #000;
  margin: 0;
  padding: 0;
  line-height: 0;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    overflow: visible;
    transform: none;
    margin-bottom: 20px;

    &:hover {
      transform: none;
    }
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }
`;

const PhotomontageImage = styled.img`
  display: block;
  height: auto;
  max-height: 400px;
  width: auto;
  object-fit: contain;
  margin: 0;
  padding: 0;
  background-color: #000;
  cursor: pointer;
`;

const ItemOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  ${GalleryItem}:hover & {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 2px;
    padding: 15px;
  }
`;

const ItemTitle = styled.h3`
  color: white;
  margin: 0 0 5px 0;
  font-size: 0.9rem;
`;

const ItemDetails = styled.div`
  font-size: 0.8rem;
  color: #ddd;
  margin: 3px 0;
`;

const ItemPrice = styled.span`
  color: white;
  font-weight: bold;
`;

const ItemBuyButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 5px;
  width: 100%;

  &:hover {
    background: #36964f;
  }
`;

const ItemStatus = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #a00;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;

  @media (max-width: 768px) {
    position: static;
    display: inline-block;
    margin-top: 5px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ccc;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ff6b6b;
  font-size: 1rem;
  gap: 15px;
`;

const RetryButton = styled.button`
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #555;
  }
`;

function Collection() {
  const { id } = useParams();
  const location = useLocation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [collection, setCollection] = useState(null);
  const [images, setImages] = useState([]);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [sourceGroup, setSourceGroup] = useState(0);
  const [nextCollection, setNextCollection] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadCollection = async () => {
      try {
        setLoading(true);
        setError(null);

        const foundCollection = await getCollectionByIdAsync(id);

        if (!mounted) return;

        if (!foundCollection) {
          console.error(`Collection not found: ${id}`);
          setError('Collection not found. Please try again.');
          setLoading(false);
          return;
        }

        setCollection(foundCollection);

        // Extract the source group from the URL
        const params = new URLSearchParams(location.search);
        const fromParam = params.get('from');
        if (fromParam !== null) {
          const groupIndex = parseInt(fromParam, 10);
          if (!isNaN(groupIndex) && groupIndex >= 0) {
            setSourceGroup(groupIndex);
          }
        }

        // Find the next collection in the overall list, regardless of group
        if (foundCollection) {
          const allCollections = await getAllCollectionsAsync();

          if (!mounted) return;

          const currentIndex = allCollections.findIndex(c => c.id === id);

          if (currentIndex !== -1 && currentIndex < allCollections.length - 1) {
            // There is a next collection in the overall list
            setNextCollection(allCollections[currentIndex + 1]);
          } else if (currentIndex !== -1) {
            // If at the end of the list, loop back to the first collection
            setNextCollection(allCollections[0]);
          }
        }

    // Define image patterns for different collections
    const getImagesForCollection = async (collection) => {
      // ALWAYS load from collections-data.json - this is the source of truth for all collections
      try {
        const response = await fetch('/collections-data.json');
        const data = await response.json();

        // Find the folder data for this collection
        const folderData = data.folders.find(f =>
          f.path.toLowerCase() === collection.folder.toLowerCase()
        );

        if (folderData && folderData.imageFiles && folderData.imageFiles.length > 0) {
          console.log(`✅ Loading ${folderData.imageFiles.length} images for ${collection.title} from filesystem`);

          // Use the actual files from the file system - this works for ALL collections
          return folderData.imageFiles.map((filename, index) => {
            // Try to find matching item metadata by imagePath
            const item = collection.items?.find(i => i.imagePath === filename) ||
                        collection.items?.[Math.min(index, (collection.items?.length || 1) - 1)] ||
                        null;

            return {
              id: index + 1,
              src: `/images/collections/${collection.folder}/${filename}`,
              alt: item?.title || `${collection.title} - Image ${index + 1}`,
              item: item || {
                title: `${collection.title} - Image ${index + 1}`,
                imagePath: filename,
                available: collection.available,
                price: collection.price,
                medium: collection.medium,
                size: collection.size,
                year: collection.year,
                status: collection.status
              }
            };
          });
        }

        console.warn(`⚠️ No images found in collections-data.json for ${collection.title}`);
      } catch (error) {
        console.error(`❌ Error loading collections-data.json for ${collection.title}:`, error);
      }

      // Fallback ONLY if collections-data.json completely fails
      // This should rarely happen - all collections should load from collections-data.json
      console.warn(`⚠️ Using fallback for ${collection.title} - this should not happen!`);
      return [];
    };

        if (foundCollection) {
          const collectionImages = await getImagesForCollection(foundCollection);

          if (!mounted) return;

          setImages(collectionImages);

          // Format images for the lightbox component
          const formattedImages = collectionImages.map(image => ({
            src: image.src,
            title: image.item ? image.item.title : `${foundCollection.title} - Image ${image.id}`,
            medium: image.item ? image.item.medium : null,
            size: image.item ? image.item.size : null,
            year: image.item ? image.item.year : null,
            available: image.item ? image.item.available : false,
            status: image.item ? image.item.status : null,
            price: image.item ? image.item.price : null,
          }));
          setLightboxImages(formattedImages);
        }
      } catch (err) {
        console.error('Error loading collection:', err);
        if (mounted) {
          setError('Failed to load collection. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCollection();

    return () => {
      mounted = false;
    };
  }, [id, location.search]);

  const openLightbox = (image, index) => {
    setSelectedImageIndex(index);
    setLightboxVisible(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxVisible(false);
    document.body.style.overflow = 'auto';
  };

  const handlePurchase = (item) => {
    setSelectedItem(item);
    setIsCheckingOut(true);
  };

  const handlePaymentSuccess = async () => {
    setPaymentComplete(true);
    setIsCheckingOut(false);

    // Reload collection to reflect the purchased item as unavailable
    try {
      const updatedCollection = await getCollectionByIdAsync(id);
      if (updatedCollection) {
        setCollection(updatedCollection);
        console.log('✅ Collection data refreshed after purchase');
      }
    } catch (err) {
      console.error('Error refreshing collection:', err);
    }
  };

  const closePaymentModal = () => {
    setIsCheckingOut(false);
    setSelectedItem(null);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <CollectionContainer>
        <LoadingContainer>Loading collection...</LoadingContainer>
      </CollectionContainer>
    );
  }

  if (error) {
    return (
      <CollectionContainer>
        <ErrorContainer>
          <div>{error}</div>
          <RetryButton onClick={handleRetry}>Retry</RetryButton>
        </ErrorContainer>
      </CollectionContainer>
    );
  }

  if (!collection) {
    return (
      <CollectionContainer>
        <ErrorContainer>
          <div>Collection not found</div>
          <NavLink to="/">Back to Home</NavLink>
        </ErrorContainer>
      </CollectionContainer>
    );
  }

  // Determine if we should use photomontage layout
  const usePhotomontage = collection.id === 'rejuvenation';

  return (
    <CollectionContainer>
      <StickyHeader>
        <CollectionTitle>{collection.title}</CollectionTitle>

        <CollectionInfo>
          <CollectionDetails>
            <DetailsList>
              {collection.year && <DetailItem><strong>Year:</strong> {collection.year}</DetailItem>}
            </DetailsList>
          </CollectionDetails>

          <NavigationContainer>
            <NavLink to={`/?group=${sourceGroup}`}>&lt; Back to Collections</NavLink>
            {nextCollection && (
              <NavLink to={`/collection/${nextCollection.id}?from=${sourceGroup}`}>
                Next Collection &gt;
              </NavLink>
            )}
          </NavigationContainer>
        </CollectionInfo>
      </StickyHeader>

      {usePhotomontage ? (
        <PhotomontageGrid>
          {images.map((image, index) => (
            <PhotomontageItem
              key={image.id}
            >
              <LazyImage
                src={image.src}
                alt={image.alt}
                objectFit="contain"
                onClick={() => openLightbox(image, index)}
                hoverEffect={false}
              />
              {image.item && (
                <FavoriteButton
                  item={{
                    ...image.item,
                    imagePath: image.item.imagePath || image.src.split('/').pop(),
                    collectionId: collection.id,
                    collectionTitle: collection.title,
                    collectionFolder: collection.folder
                  }}
                />
              )}
              {image.item && !image.item.available && image.item.status && (
                <ItemStatus>{image.item.status}</ItemStatus>
              )}
              <ItemOverlay>
                <ItemTitle>{image.item ? image.item.title : `Item ${image.id}`}</ItemTitle>

                {image.item && image.item.medium && (
                  <ItemDetails>{image.item.medium}</ItemDetails>
                )}
                {image.item && image.item.size && (
                  <ItemDetails>{image.item.size}</ItemDetails>
                )}

                {image.item && image.item.available && image.item.price ? (
                  <>
                    <ItemPrice>${image.item.price}</ItemPrice>
                    <ItemBuyButton onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(image.item);
                    }}>
                      Purchase Now
                    </ItemBuyButton>
                  </>
                ) : null}
              </ItemOverlay>
            </PhotomontageItem>
          ))}
        </PhotomontageGrid>
      ) : (
        <GalleryGrid>
          {images.map((image, index) => (
            <GalleryItem key={image.id}>
              <LazyImage
                src={image.src}
                alt={image.alt}
                hoverEffect={!isMobile}
                onClick={() => openLightbox(image, index)}
              />
              {image.item && (
                <FavoriteButton
                  item={{
                    ...image.item,
                    imagePath: image.item.imagePath || image.src.split('/').pop(),
                    collectionId: collection.id,
                    collectionTitle: collection.title,
                    collectionFolder: collection.folder
                  }}
                />
              )}
              {image.item && !image.item.available && image.item.status && (
                <ItemStatus>{image.item.status}</ItemStatus>
              )}
              <ItemOverlay>
                <ItemTitle>{image.item ? image.item.title : `Item ${image.id}`}</ItemTitle>

                {image.item && image.item.medium && (
                  <ItemDetails>{image.item.medium}</ItemDetails>
                )}
                {image.item && image.item.size && (
                  <ItemDetails>{image.item.size}</ItemDetails>
                )}

                {image.item && image.item.available && image.item.price ? (
                  <>
                    <ItemPrice>${image.item.price}</ItemPrice>
                    <ItemBuyButton onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(image.item);
                    }}>
                      Purchase Now
                    </ItemBuyButton>
                  </>
                ) : null}
              </ItemOverlay>
            </GalleryItem>
          ))}
        </GalleryGrid>
      )}

      {/* Enhanced Lightbox Component */}
      <ImageLightbox
        images={lightboxImages}
        initialIndex={selectedImageIndex || 0}
        onClose={closeLightbox}
        visible={lightboxVisible}
      />

      {isCheckingOut && selectedItem && (
        <PaymentModal
          artwork={selectedItem}
          onClose={closePaymentModal}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {paymentComplete && (
        <SuccessMessage>
          Thank you for your purchase! We will contact you shortly to arrange delivery.
        </SuccessMessage>
      )}
    </CollectionContainer>
  );
}

export default Collection;